<template>
  <div :id="`${source}_meeting_analytics_table`">
    <ContainerInfoItem
      :source="`${source}_meeting_analytics_table`"
      title="Meeting activity by user"
      :description="range?.label"
      height="fit-content"
    >
      <SkeletonLoader v-if="isLoading" :page="loaderPages.TABLE" />

      <CustomTable
        v-else-if="overviews?.length > 0"
        :headers="meetingAnalyticsTableHeaders"
        :data="overviews"
        :show-page-size="false"
        :pagination="pagination"
        @update:pagination="handlePageChange"
        class="mt-4"
      />

      <EmptyScreen
        v-else
        :source="`${source}_meeting_analytics_table`"
        text="No data found!"
        :type="emptyScreenTypes.CHART_AND_TABLE"
      />
    </ContainerInfoItem>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watchEffect } from "vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import { emptyScreenTypes } from "@/src/constants/searchConstants.js";
import { meetingAnalyticsTableHeaders } from "@/src/constants/analyticConstants.js";
import store from "@/src/store/index.js";
import AnalyticService from "@/src/api/AnalyticService.js";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import CustomTable from "@/src/components/custom_table_components/CustomTable.vue";
import ContainerInfoItem from "@/src/components/info_components/ContainerInfoItem.vue";
import { useAnalyticsRefresh } from "@/src/composables/useAnalyticsRefresh.js";

const props = defineProps({
  source: { type: String, required: true },
});

const range = computed(() => store.getters["analytics/getRange"]);
const filters = computed(() => store.getters["analytics/getFilters"]);
const userIds = computed(() => store.getters["analytics/getUserIds"]);
const teamIds = computed(() => store.getters["analytics/getTeamIds"]);

const isLoading = ref(false);
const overviews = ref([]);
const pagination = ref({
  current: 1,
  pageSize: 5,
  total: 0,
});

const handlePageChange = (newPagination) => {
  try {
    pagination.value = { ...pagination.value, current: newPagination.current };
    toFetchMeetingUserOverview();
  } catch (error) {
    console.error("An error occurred while handling page change: ", error);
  }
};

const toFetchMeetingUserOverview = async () => {
  try {
    const params = {
      action: "created",
      order: "desc",
      start_time: range.value?.start,
      end_time: range.value?.end,
      page: pagination.value.current,
      size: pagination.value.pageSize,
      user_id: userIds.value,
      team_id: teamIds.value,
      meeting_agent_id: filters.value?.meeting_agent?.id,
    };

    const response = await AnalyticService.V2.fetchMeetingUserOverview(params);
    if (response?.success && response?.data) {
      const data = response?.data?.data;

      overviews.value = data?.overviews;
      pagination.value.total = data?.pagination?.total;
    }
  } catch (error) {
    console.error(
      "An error occurred while fetching meeting user overview: ",
      error,
    );
  }
};

watchEffect(() => {
  if (range.value && userIds.value && teamIds.value) {
    toFetchMeetingUserOverview();
  }
});

useAnalyticsRefresh(toFetchMeetingUserOverview, [range, userIds, teamIds]);

onMounted(() => {
  toFetchMeetingUserOverview();
});
</script>
